import React, { useState, useCallback } from 'react';
import styles from './CorrectionForm.module.css';

export type CorrectionCategory =
  | 'pricing'
  | 'scope'
  | 'product'
  | 'installation'
  | 'accounting';

export interface CorrectionFormData {
  originalValue: string;
  correctedValue: string;
  category: CorrectionCategory;
  notes: string;
}

export interface CorrectionFormProps {
  originalValue: string;
  messageId: string;
  skill?: string;
  onSubmit: (data: CorrectionFormData) => void;
  onCancel: () => void;
}

const CATEGORY_OPTIONS: { value: CorrectionCategory; label: string }[] = [
  { value: 'pricing', label: 'Pricing' },
  { value: 'scope', label: 'Scope' },
  { value: 'product', label: 'Product' },
  { value: 'installation', label: 'Installation' },
  { value: 'accounting', label: 'Accounting' },
];

export const CorrectionForm: React.FC<CorrectionFormProps> = ({
  originalValue,
  onSubmit,
  onCancel,
}) => {
  const [correctedValue, setCorrectedValue] = useState('');
  const [category, setCategory] = useState<CorrectionCategory>('pricing');
  const [notes, setNotes] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const isValid = correctedValue.trim().length > 0;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValid) return;
      onSubmit({
        originalValue,
        correctedValue: correctedValue.trim(),
        category,
        notes: notes.trim(),
      });
    },
    [isValid, onSubmit, originalValue, correctedValue, category, notes]
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.fieldGroup}>
        <label className={styles.label}>Original Value</label>
        <div className={styles.originalValue}>{originalValue}</div>
      </div>

      <div className={styles.arrow}>&#8595;</div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Corrected Value</label>
        <textarea
          className={`${styles.textarea} ${styles.correctedHighlight}`}
          value={correctedValue}
          onChange={(e) => setCorrectedValue(e.target.value)}
          placeholder="Enter the correct value..."
          autoFocus
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Category</label>
        <select
          className={styles.select}
          value={category}
          onChange={(e) => setCategory(e.target.value as CorrectionCategory)}
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Notes (Optional)</label>
        <textarea
          className={styles.textarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional context for this correction..."
          rows={2}
        />
      </div>

      {showPreview && isValid && (
        <div className={styles.preview}>
          <div className={styles.previewHeader}>Correction Preview</div>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>Original:</span>
            <span className={styles.previewValue}>{originalValue}</span>
          </div>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>Corrected:</span>
            <span className={styles.previewValue}>{correctedValue}</span>
          </div>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>Category:</span>
            <span className={styles.previewValue}>
              {CATEGORY_OPTIONS.find((o) => o.value === category)?.label}
            </span>
          </div>
          {notes.trim() && (
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>Notes:</span>
              <span className={styles.previewValue}>{notes}</span>
            </div>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.btnPreview}
          onClick={() => setShowPreview((prev) => !prev)}
          disabled={!isValid}
        >
          {showPreview ? 'Hide Preview' : 'Preview'}
        </button>
        <button type="submit" className={styles.btnPrimary} disabled={!isValid}>
          Submit Correction
        </button>
      </div>
    </form>
  );
};
